import Button from "@ch/components/Buttons/Button";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import { type FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import KemChatComposerSection from "./components/KemChatComposerSection";
import KemChatInput from "./components/KemChatInput";
import KemChatPanel from "./components/KemChatPanel";
import KemChatPrivacyToolbar from "./components/KemChatPrivacyToolbar";
import KemHealthUnavailableBanner from "./components/KemHealthUnavailableBanner";
import KemICSessionCompleteModal from "./components/KemICSessionCompleteModal";
import KemPageShell from "./components/KemPageShell";
import KemSessionProgressBar from "./components/KemSessionProgressBar";
import QuickOptions from "./components/QuickOptions";
import KemTour from "./components/KemTour";
import useKemCoachChat from "./hooks/useKemCoachChat";
import { useKemChatScroll } from "./hooks/useKemChatScroll";
import useLockBodyScroll from "./hooks/useLockBodyScroll";
import { useViewportUnderHeader } from "./hooks/useViewportUnderHeader";
import type { TQuickOption } from "./utils/types";

const KEM_IC_COMPLETION_PENDING_STORAGE_KEY = "kem_ic_completion_pending";

const KemICPage: FC = () => {
  const navigate = useNavigate();
  const { outerStyle } = useViewportUnderHeader();
  useLockBodyScroll();

  const kemUserSession = useAppSelector((state) => state.kem.kemUserSession);
  const icCompletionProgress = useAppSelector(
    (state) => state.kem.icCompletionProgress,
  );
  const currentWeek = kemUserSession?.week ?? "-1";

  const {
    currentChat,
    handleSendMessage,
    handleGetNextRecentHistory,
    getIsLastPage,
    isLoadingHistory,
    isInitialHistoryLoading,
    isLoadingMoreHistory,
    loadingBotChat,
    kemIsTyping,
    kemSocketReachable,
    isConnecting,
    onCloseSessionCompletedModal,
    isSessionCompletedModalOpen,
    sessionCompleted,
    scrollContainerRef: coachScrollContainerRef,
    setLatestMessageFollowScroll,
    currentQuickOptions,
    setCurrentQuickOptions,
  } = useKemCoachChat({ currentWeek });

  const [kemICSessionCompleteModalId] = useState(
    "kem-ic-session-complete-modal",
  );

  const getCanPaginateAtTop = useCallback(
    () => !isLoadingHistory && currentChat.length > 0 && !getIsLastPage(),
    [currentChat.length, getIsLastPage, isLoadingHistory],
  );

  const {
    combinedChatRef,
    onScroll,
    queueScrollToBottomAfterUpdate,
    scrollToBottom,
    showScrollToBottom,
    handleKemBubbleImageLoad,
  } = useKemChatScroll({
    currentChat,
    loadingBotChat,
    kemIsTyping,
    isLoadingHistory,
    isLoadingMoreHistory,
    handleGetNextRecentHistory,
    getCanPaginateAtTop,
    coachScrollContainerRef,
    fallbackScrollToBottom: () => {},
  });

  useEffect(() => {
    setLatestMessageFollowScroll(queueScrollToBottomAfterUpdate);
    return () => {
      setLatestMessageFollowScroll(null);
    };
  }, [queueScrollToBottomAfterUpdate, setLatestMessageFollowScroll]);

  const handleSessionCompleteModalClose = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(KEM_IC_COMPLETION_PENDING_STORAGE_KEY);
      window.location.reload();
    }
    onCloseSessionCompletedModal();
  }, [onCloseSessionCompletedModal]);

  const handleChatWithKem = useCallback(() => {
    void navigate("/kem/chat");
  }, [navigate]);

  const handleSelectQuickOption = useCallback(
    (option: TQuickOption) => {
      void handleSendMessage({ message: option.label });
      setCurrentQuickOptions([]);
    },
    [handleSendMessage, setCurrentQuickOptions],
  );

  const shouldShowSessionCompletedModal = isSessionCompletedModalOpen;

  const afterCard = (
    <>
      <KemTour />
      {shouldShowSessionCompletedModal && (
        <KemICSessionCompleteModal
          modalId={kemICSessionCompleteModalId}
          onClose={handleSessionCompleteModalClose}
        />
      )}
    </>
  );

  const isCenterLoading = (isConnecting ?? false) || isInitialHistoryLoading;

  return (
    <KemPageShell afterCard={afterCard} outerStyle={outerStyle}>
      <KemHealthUnavailableBanner visible={!kemSocketReachable} />

      <div className="relative flex min-h-0 flex-1 flex-col pt-2">
        <KemChatPrivacyToolbar />

        {!sessionCompleted && (
          <KemSessionProgressBar
            accessibilityLabel="Intake completion progress"
            className="mb-4"
            progress={icCompletionProgress}
          />
        )}

        <KemChatPanel
          combinedChatRef={combinedChatRef}
          isCenterLoading={isCenterLoading}
          isEmpty={currentChat.length === 0}
          messages={currentChat}
          onKemBubbleImageLoad={handleKemBubbleImageLoad}
          onScroll={onScroll}
          showTopPaginationLoader={!isLoadingHistory && isLoadingMoreHistory}
        />
      </div>

      <KemChatComposerSection
        inputArea={
          sessionCompleted ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-stretch gap-3">
                <div className="bg-rose-50 flex min-h-[48px] min-w-[140px] flex-1 items-center rounded-[6px] px-4 py-3">
                  <p className="text-sm font-semibold text-ink-900">
                    Session Completed!
                  </p>
                </div>
                <Button
                  className="shrink-0 self-center rounded-full px-6 py-3 font-semibold"
                  onClick={handleChatWithKem}
                  type="button"
                >
                  Chat with Kem
                </Button>
              </div>
              <p className="text-center text-xs text-ink-700">
                I am an AI assistant and cannot provide medical advice. Consult
                a doctor for clinical concerns.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {currentQuickOptions.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-ink-700">
                    Quick options:
                  </p>
                  <QuickOptions
                    isDisabled={Boolean(loadingBotChat) || !kemSocketReachable}
                    onSelectOption={handleSelectQuickOption}
                    options={currentQuickOptions}
                  />
                </div>
              )}
              <KemChatInput
                disabled={Boolean(loadingBotChat) || !kemSocketReachable}
                onSend={(text) => {
                  void handleSendMessage({ message: text });
                }}
              />
            </div>
          )
        }
        kemIsTyping={kemIsTyping}
        onScrollToNewer={() => scrollToBottom()}
        showScrollToBottom={showScrollToBottom}
      />
    </KemPageShell>
  );
};

export default KemICPage;
